import axiosInstance from '../api/axios';
import Cookies from 'js-cookie';
import { store } from '../../store';
import { setUser, setError, clearError, logout } from '../../store/slices/authSlice';
import axios from 'axios';
import { AuthResponse } from '../../types/common';

const BASE_URL = import.meta.env.VITE_API_BASE_URL;

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

export const authService = {
  login: async (username: string, password: string, totpCode?: string, trustDevice = false) => {
    try {
      clearError();
      const response = await axiosInstance.post<AuthResponse>('/auth/login', {
        username,
        password,
        totp_code: totpCode || undefined,
        trust_device: trustDevice
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.data.access_token || !response.data.refresh_token) {
        throw new Error('No tokens received');
      }

      // Сохраняем оба токена (session cookies — очищаются при logout)
      Cookies.set('access_token', response.data.access_token, {
        secure: true,
        sameSite: 'strict'
      });

      Cookies.set('refresh_token', response.data.refresh_token, {
        secure: true,
        sameSite: 'strict'
      });

      const userData = await axiosInstance.get('/auth/me');
      store.dispatch(setUser(userData.data));
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const detail = error.response?.data?.detail;
        const message = typeof detail === 'object' ? detail.message : (detail || 'Ошибка авторизации');
        store.dispatch(setError(message));
        const authError = new Error(message) as Error & { code?: string };
        authError.code = typeof detail === 'object' ? detail.code : undefined;
        throw authError;
      }
      store.dispatch(setError('Неизвестная ошибка'));
      throw error;
    }
  },

  setTokens: (tokens: AuthResponse) => {
    Cookies.set('access_token', tokens.access_token, {
      secure: true,
      sameSite: 'strict'
    });

    if (tokens.refresh_token) {
      Cookies.set('refresh_token', tokens.refresh_token, {
        secure: true,
        sameSite: 'strict'
      });
    }
  },

  refreshToken: async (): Promise<AuthResponse> => {
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      });
    }

    isRefreshing = true;

    try {
      const refreshToken = Cookies.get('refresh_token');
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await axios.post<AuthResponse>(
        `${BASE_URL}/auth/refresh_token`,
        { refresh_token: refreshToken },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      authService.setTokens(response.data);
      processQueue(null, response.data.access_token);
      return response.data;
    } catch (error) {
      processQueue(error);
      store.dispatch(logout());
      throw error;
    } finally {
      isRefreshing = false;
    }
  },
  
  logout: () => {
    store.dispatch(logout());
    Cookies.remove('access_token', { 
      secure: true, 
      sameSite: 'strict' 
    });
    Cookies.remove('refresh_token', {
      secure: true,
      sameSite: 'strict'
    });
    window.location.href = '/login';
  },
  
  me: async () => {
    try {
      const response = await axiosInstance.get('/auth/me');
      store.dispatch(setUser(response.data));
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        store.dispatch(logout());
      }
      throw error;
    }
  },

  setup2fa: async () => (await axiosInstance.post('/auth/me/2fa/setup')).data,
  confirm2fa: async (code: string) => (await axiosInstance.post('/auth/me/2fa/confirm', { code })).data,
  disable2fa: async (code: string) => (await axiosInstance.post('/auth/me/2fa/disable', { code })).data,
  changePassword: async (currentPassword: string, newPassword: string, totpCode?: string) => (
    await axiosInstance.patch('/auth/me/password', {
      current_password: currentPassword,
      new_password: newPassword,
      totp_code: totpCode || undefined,
    })
  ).data,
};
